import React, { useState, useEffect } from 'react';
import { Star, Users, CalendarDays, Play, ChevronRight, ShieldCheck } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Acne Treatment", "Pigmentation", "Hair Transplant", "Laser Treatment",
  "Botox", "Chemical Peeling", "Skin Diseases", "Scar Treatment"
];

export default function HeroSection() {
  const { t } = useLanguage();
  const [date, setDate] = useState(undefined);
  const [formData, setFormData] = useState({ name: '', phone: '' , service: '' });
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [timeSlot, setTimeSlot] = useState('');

  useEffect(() => {
    if (!date) return;
    const fetchSlots = async () => {
      try {
        const dateStr = date.toISOString().split('T')[0];
        const res = await axios.get(`${API}/time-slots?date=${dateStr}`);
        setAvailableSlots(res.data.available_slots || []);
        setTimeSlot('');
      } catch (err) {}
    };
    fetchSlots();
  }, [date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service || !date || !timeSlot) return;
    setSubmitting(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const phone = formData.phone.startsWith('+91') ? formData.phone.slice(3) : formData.phone;

      const msg = `*New Appointment Booked*%0A%0A*Patient:* ${formData.name}%0A*Phone:* ${phone}%0A*Treatment:* ${formData.service}%0A*Date:* ${dateStr}%0A*Time:* ${timeSlot}%0A%0A_Booked via DivyadeepClinic.com_`;
      const waUrl = `https://api.whatsapp.com/send?phone=918303365990&text=${msg}`;

      await axios.post(`${API}/appointments`, {
        name: formData.name,
        phone: phone,
        service: formData.service,
        date: dateStr,
        time_slot: timeSlot
      });

      window.location.href = waUrl;
    } catch (err) {
      if (err.response?.status === 422) {
        const detail = err.response.data?.detail;
        if (Array.isArray(detail) && detail.length > 0) {
          alert(detail[0]?.msg || 'Please check your input');
        } else {
          alert('Please check your input and try again');
        }
      } else {
        alert('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <section id="home" className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/7108227/pexels-photo-7108227.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt="Best dermatologist in Jhansi - skin care treatment at Divyadeep Skin Clinic"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/70 to-[#0F172A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 via-transparent to-[#0F172A]/20" />
        <div className="absolute inset-0 bg-[#D1A760]/[0.04]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-12 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-start">

          {/* LEFT: TEXT CONTENT */}
          <div className="lg:col-span-7 space-y-7 pt-4">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 opacity-0 animate-[fadeSlideUp_0.6s_0.2s_cubic-bezier(0.16,1,0.3,1)_forwards]" data-testid="trust-badges">
              <div className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
                <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#D1A760] fill-[#D1A760]" />
                <span className="text-xs md:text-sm font-manrope font-semibold text-white">{t('hero_rating')}</span>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
                <Users className="w-3.5 h-3.5 md:w-4 md:h-4 text-white/70" />
                <span className="text-xs md:text-sm font-manrope font-medium text-white/80">{t('hero_reviews')}</span>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10">
                <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-400" />
                <span className="text-xs md:text-sm font-manrope font-medium text-white/80">IADVL Certified</span>
              </div>
            </div>

            <div className="opacity-0 animate-[fadeSlideUp_0.7s_0.4s_cubic-bezier(0.16,1,0.3,1)_forwards]">
              <h1 className="font-outfit font-light text-3xl sm:text-4xl lg:text-[3.5rem] xl:text-[4rem] text-white tracking-tight leading-[1.08]" data-testid="hero-headline">
                {t('hero_headline')}
              </h1>
              <p className="mt-3 md:mt-4 text-base md:text-lg text-[#D1A760] font-outfit font-light">
                Clear Skin. Confident You.
              </p>
              <p className="mt-4 md:mt-5 text-sm md:text-base text-white/60 font-manrope leading-relaxed max-w-lg">
                {t('hero_subtext')}
              </p>
            </div>

            <div className="flex items-center gap-4 p-4 pr-8 rounded-2xl bg-white/[0.07] backdrop-blur-xl border border-white/10 w-fit opacity-0 animate-[fadeSlideUp_0.6s_0.6s_cubic-bezier(0.16,1,0.3,1)_forwards]">
              <img
                src="https://customer-assets.emergentagent.com/job_kuldeep-skin/artifacts/raia4m08_WhatsApp%20Image%202026-04-12%20at%2014.48.31.jpeg"
                alt="Dr. Kuldeep Verma - Best Dermatologist in Jhansi"
                className="w-16 h-16 rounded-2xl object-cover object-[center_20%] ring-2 ring-[#D1A760]/40 shadow-lg"
                data-testid="doctor-image-hero"
              />
              <div>
                <p className="font-outfit font-medium text-white text-base">Dr. Kuldeep Verma</p>
                <p className="text-sm text-white/50 font-manrope">MD, DNB — Dermatologist & Hair Transplant Surgeon</p>
                <div className="flex gap-0.5 mt-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-3 h-3 text-[#D1A760] fill-[#D1A760]" />
                  ))}
                  <span className="text-[10px] text-white/40 font-manrope ml-1">4.9</span>
                </div>
              </div>
            </div>

            <div className="flex gap-6 md:gap-10 pt-2 opacity-0 animate-[fadeSlideUp_0.6s_0.8s_cubic-bezier(0.16,1,0.3,1)_forwards]">
              <div>
                <p className="font-outfit font-semibold text-2xl md:text-3xl text-white">25+</p>
                <p className="text-[10px] md:text-xs text-white/40 font-manrope mt-0.5">Research Papers</p>
              </div>
              <div className="w-px h-10 md:h-12 bg-white/10 self-center" />
              <div>
                <p className="font-outfit font-semibold text-2xl md:text-3xl text-white">10+</p>
                <p className="text-[10px] md:text-xs text-white/40 font-manrope mt-0.5">Years Experience</p>
              </div>
              <div className="w-px h-10 md:h-12 bg-white/10 self-center" />
              <div>
                <p className="font-outfit font-semibold text-2xl md:text-3xl text-[#D1A760]">4.9</p>
                <p className="text-[10px] md:text-xs text-white/40 font-manrope mt-0.5">Patient Rating</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2 opacity-0 animate-[fadeSlideUp_0.6s_1s_cubic-bezier(0.16,1,0.3,1)_forwards]">
              <button
                onClick={() => document.querySelector('#appointment')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-3.5 rounded-full bg-gradient-to-r from-[#D1A760] to-[#B89255] text-white font-manrope font-semibold text-sm hover:shadow-[0_8px_30px_rgba(209,167,96,0.35)] transition-all duration-300 flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Book Appointment <ChevronRight className="w-4 h-4" />
              </button>
              <a
                href="tel:+918303365990"
                className="px-6 py-3.5 rounded-full border border-white/20 text-white/80 font-manrope font-medium text-sm hover:bg-white/10 hover:text-white transition-all duration-200 flex items-center gap-2"
                data-testid="hero-cta-call"
              >
                <Play className="w-3.5 h-3.5 fill-white/80" /> Call 083033 65990
              </a>
            </div>
          </div>

          {/* RIGHT: BOOKING FORM */}
          <div className="lg:col-span-5 w-full max-w-md mx-auto lg:ml-auto opacity-0 animate-[fadeSlideUp_0.8s_0.5s_cubic-bezier(0.16,1,0.3,1)_forwards]" data-testid="booking-form-container">
            <div className="bg-white/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(0,0,0,0.3)] rounded-3xl p-6 md:p-7 ring-1 ring-white/20">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D1A760] to-[#B89255] flex items-center justify-center">
                  <CalendarDays className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="font-outfit font-medium text-base text-[#0F172A]">{t('nav_book')}</h3>
                  <p className="text-[10px] text-[#94A3B8] font-manrope">Quick book in 30 seconds</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3" data-testid="hero-booking-form">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl bg-[#F8FAFC] border border-slate-200 text-sm font-manrope text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#D1A760] focus:ring-2 focus:ring-[#D1A760]/10 transition-all"
                  data-testid="booking-name-input"
                  required
                />
                <div className="flex items-center gap-0 rounded-xl bg-[#F8FAFC] border border-slate-200 focus-within:border-[#D1A760] focus-within:ring-2 focus-within:ring-[#D1A760]/10 transition-all">
                  <span className="pl-4 text-sm font-manrope text-[#475569] select-none">+91</span>
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                    className="w-full px-2 py-3 bg-transparent text-sm font-manrope text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none"
                    data-testid="booking-phone-input"
                    required
                  />
                </div>

                <Select value={formData.service} onValueChange={val => setFormData({...formData, service: val})}>
                  <SelectTrigger className="w-full px-4 py-3 h-auto rounded-xl bg-[#F8FAFC] border-slate-200 text-sm font-manrope" data-testid="booking-service-select">
                    <SelectValue placeholder="Select Treatment" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div className="bg-[#F8FAFC] rounded-xl p-2.5 border border-slate-200" data-testid="booking-calendar">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return d < today || d.getDay() === 0;
                    }}
                    className="mx-auto"
                  />
                </div>

                {date && availableSlots.length > 0 && (
                  <div data-testid="hero-time-slots">
                    <p className="text-xs font-manrope font-medium text-[#475569] mb-2">Select Time</p>
                    <div className="grid grid-cols-3 gap-1.5 max-h-[120px] overflow-y-auto">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setTimeSlot(slot)}
                          className={`px-2 py-2 rounded-lg text-[11px] font-manrope transition-all ${
                            timeSlot === slot
                              ? 'bg-[#0F172A] text-white'
                              : 'bg-white text-[#475569] border border-slate-200 hover:border-[#D1A760]'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !formData.name || !formData.phone || !formData.service || !date || !timeSlot}
                  data-testid="hero-book-btn"
                  className="w-full py-3.5 rounded-full bg-[#25D366] text-white font-manrope font-semibold text-sm hover:bg-[#1da851] hover:shadow-[0_4px_20px_rgba(37,211,102,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {submitting ? 'Booking...' : 'Confirm on WhatsApp'}
                </button>
              </form>

              <p className="text-center text-[10px] text-[#94A3B8] font-manrope mt-3">
                Mon-Sat: 11:00 AM - 5:30 PM | Sunday Closed
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FAFAFA] z-10" />
    </section>
  );
}
